<template>
  <div class="ins-gr" @pointermove="onTip" @pointerleave="hideTip">
    <div v-if="!tree.length" class="dempty">{{ noBlocks ? 'Ни одна из ' + noBlocks + ' ' + plural(noBlocks, ['сессии', 'сессий', 'сессий']) + ' среза не дошла ни до одного блока, а такие сессии в решётку не входят.' : 'В срезе нет сессий.' }}</div>
    <template v-else>
      <div class="ins-gr-bar">
        <label>клетка</label>
        <span class="ins-gr-seg">
          <button type="button" class="tgl" :aria-pressed="String(cell === 'share')" @click="cell = 'share'">доли</button>
          <button type="button" class="tgl" :aria-pressed="String(cell === 'n')" @click="cell = 'n'">штуки</button>
        </span>
        <span class="ins-gr-dev">
          <label>раскрыть</label>
          <span class="ins-gr-seg">
            <button v-for="d in 3" :key="d" type="button" class="tgl" :aria-pressed="String(depth === d)"
                    :title="depthTitle[d - 1]" @click="setDepth(d)">{{ ['только тексты', 'до слов', 'до фраз'][d - 1] }}</button>
          </span>
        </span>
        <!-- Числовые колонки: у каждой своя кнопка, своя сортировка и своя медиана уровня.
             Список один на кнопки, шапку и клетки, поэтому подписи не разойдутся. -->
        <label>колонки</label>
        <span class="ins-gr-seg">
          <button v-for="c in COLS" :key="c.field" type="button" class="tgl" :aria-pressed="String(cols[c.field])"
                  :data-help="c.help"
                  :title="(cols[c.field] ? 'Скрыть' : 'Показать') + ' колонку «' + c.name + '».' + (cols[c.field] ? ' Сортировка по ней при этом снимется.' : '')"
                  @click="toggleCol(c.field)">{{ c.name }}</button>
        </span>
        <button type="button" class="tgl" :aria-pressed="String(diff)" @click="toggleDiff">разность к опоре</button>
        <button v-if="ref !== null" type="button" class="tgl" @click="unref">снять опору</button>
        <!-- Подпись и кнопки одной группой (П span#grDev): переносятся вместе. -->
        <span class="ins-gr-dev">
          <label>отклонение</label>
          <span class="ins-gr-seg">
            <button v-for="d in DEVS" :key="d.k" type="button" class="tgl" :aria-pressed="String(dev[d.k])"
                    :data-help="'решётка/' + d.name" @click="toggleDev(d.k)">{{ d.name }}</button>
          </span>
        </span>
        <button type="button" class="tgl" :aria-pressed="String(segMode)" data-help="решётка/собрать строку" @click="toggleSegMode">собрать строку</button>
        <template v-if="assemblies.length">
          <span class="ins-gr-dev">
            <label>отметка сборки</label>
            <span class="ins-gr-seg">
              <button type="button" class="tgl" :aria-pressed="String(marks.lift)" @click="marks.lift = !marks.lift">приподнята</button>
              <button type="button" class="tgl" :aria-pressed="String(marks.out)" @click="marks.out = !marks.out">выдвинута</button>
            </span>
          </span>
        </template>
      </div>

      <HeadGridSegBar v-if="segMode"
        :assemblies="assemblies" :editId="segEdit" :atoms="curAtoms" :every="every"
        :fullName="curFullName" :shortName="curShortName" :nameMax="nameMax" :tabTitle="asmFullName" :phColor="phColorAny"
        @tab="segEdit = segEdit === $event ? null : $event" @new="onNewAsm" @rename="onRename"
        @commit="onCommit" @done="segEdit = null" @atoms="setAtoms" />

      <!-- Стопка стоит НАД решёткой: её собирают кликом по строке, и ответ должен
           появляться там, куда уже смотрят. -->
      <div v-if="stack" class="ins-gr-stack">
        <div class="ins-gr-phead"><h2>Стопка уровней</h2>
          <span class="ins-gr-tag">{{ refRow ? 'одна ветка сверху донизу' : 'кликните по строке в таблице' }}</span></div>
        <table class="ins-gr-t">
          <thead><tr>
            <th class="ins-gr-name">уровень ветки</th>
            <th v-for="k in nb" :key="k">{{ k }}</th>
            <th v-for="c in visCols" :key="c.field" class="ins-gr-metric" :class="c.cls">{{ c.head }}</th>
          </tr></thead>
          <tbody>
            <tr v-for="(s, j) in stack" :key="j">
              <td class="ins-gr-name"><span class="ins-gr-lvtag">{{ s.tag }}</span><b>{{ s.r.label }}</b><s v-if="s.r.sub">{{ s.r.sub }}</s></td>
              <td v-for="(c, k) in s.cells" :key="k" class="ins-gr-cell">
                <div :style="c.style">{{ c.text }}<em v-if="c.d" :class="c.d.cls">{{ c.d.text }}</em></div>
              </td>
              <td v-for="m in s.metrics" :key="m.field" class="ins-gr-metric" :class="m.cls">
                <b>{{ m.print }}</b><em v-if="m.d" :class="m.d.cls">{{ m.d.text }}</em>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="ins-gr-note">{{ refRow
          ? 'Верхняя строка: весь срез, ниже каждый следующий уровень выбранной ветки. Крупное число: доля дошедших до блока, мелкое рядом: на сколько пунктов этот уровень разошёлся с тем, что прямо над ним. Красное: число выше, чем у уровня прямо над ним, синее: ниже, ±0: идут вместе. Для долей «выше» значит «держит лучше», а для оттока наоборот: выше значит уходят быстрее. Ступеней в стопке столько, сколько их до выбранной строки: кликните по фразе, и увидите всю цепочку срез → текст → слово → фраза, кликните по тексту, и только его.'
          : 'Пока показан только весь срез: ветку задаёт опорная строка. Кликните по любой строке таблицы: она станет опорой, и здесь появится её цепочка от среза до самой строки.' }}</div>
      </div>

      <div v-if="dev.med && visCols.length" class="ins-gr-mdleg">
        <b>В {{ visCols.length > 1 ? 'числовых колонках справа' : 'колонке «' + visCols[0].name + '»' }}:</b>
        <span class="ins-gr-it"><span class="ins-gr-sam">{{ legend.sample }}</span>значение строки</span>
        <span class="ins-gr-it"><span class="ins-gr-sam" style="color:var(--div-pos)">+7</span>{{ legend.chip }}</span>
        <span class="ins-gr-it"><span class="ins-gr-sam ins-gr-sambar"><i></i></span>строка на фоне своего уровня: залито до её значения, засечка это медиана, вся дорожка до наибольшего на уровне</span>
      </div>

      <div class="ins-gr-wrap" ref="wrap">
        <table class="ins-gr-t ins-gr-main" :class="{ 'is-fixname': nameW }" :style="nameW ? { '--namew': nameW + 'px' } : null">
          <thead><tr>
            <th class="ins-gr-name ins-gr-b" :class="{ 'is-sorted': sortField === 'name' }" @click="onHead('name')">
              текст → слово → фраза{{ arrow('name') }}
              <i class="ins-gr-rz" :class="{ 'is-drag': dragging }" title="Потянуть: изменить ширину колонки, текст переносится по словам. Двойной клик: вернуть ширину по содержимому."
                 @click.stop @dblclick.stop="nameW = null" @pointerdown="startDrag"></i>
            </th>
            <th v-for="k in nb" :key="k" class="ins-gr-b" :class="{ 'is-sorted': sortField === 'b:' + (k - 1) }"
                :title="String(blocks[k - 1])" @click="onHead('b:' + (k - 1))">{{ k }}{{ arrow('b:' + (k - 1)) }}</th>
            <th v-for="c in visCols" :key="c.field" class="ins-gr-b ins-gr-metric" :class="[c.cls, { 'is-sorted': sortField === c.field }]"
                :title="c.tip" @click="onHead(c.field)">{{ c.head }}{{ arrow(c.field) }}<span v-if="dev.med" class="ins-gr-mdh">медиана уровня: {{ medHead(c) }}</span></th>
          </tr></thead>
          <tbody>
            <tr v-for="it in view" :key="it.r.key" :class="it.cls" :data-root="it.root" @click="onRow(it.r)" tabindex="0" @keydown.enter.self="onRow(it.r)"
                :title="rowTitle(it.r)">
              <td class="ins-gr-name" :style="{ paddingLeft: (8 + it.r.level * 20) + 'px' }">
                <span class="ins-gr-nm"><span class="ins-gr-row">
                  <button v-if="it.r.children.length" type="button" class="ins-gr-tw" :aria-expanded="String(open.has(it.r.key))"
                          :title="open.has(it.r.key) ? 'свернуть всю ветку' : 'развернуть на уровень, с Alt или Shift всю ветку до конца'"
                          @click.stop="toggleRow(it.r, $event)">{{ open.has(it.r.key) ? '▾' : '▸' }}</button>
                  <i v-else class="ins-gr-tw-none"></i>
                  <i v-if="it.r.kind === 'phrase'" class="ins-gr-sw" :style="{ background: phColorOf(it.r) }"></i>
                  <span class="ins-gr-ttl" :title="ttlTip(it.r)" :data-help="it.r.key === 'нет данных' ? 'решётка/нет данных' : null">{{ it.r.label }}</span>
                  <i v-if="ref === it.r.key" class="ins-gr-refmark">опора</i>
                  <span v-if="it.r.kind === 'asm'" class="ins-gr-tools">
                    <button type="button" class="ins-gr-tb ins-gr-pin" :class="{ 'is-on': it.r.asm.pin }"
                            :title="it.r.asm.pin ? 'Закреплена внизу и вне сортировок. Клик: отпустить в общий список.' : 'Участвует в сортировках наравне со строками. Клик: закрепить внизу.'"
                            @click.stop="it.r.asm.pin = !it.r.asm.pin">
                      <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true"><path fill="currentColor" d="M9.6 1.2 14.8 6.4l-1.1 1.1-1.2-.3-2.6 2.6.5 2.2c.1.4 0 .8-.3 1.1l-.5.5-3.6-3.6-3.4 3.4-.7-.7 3.4-3.4L1.7 5.7l.5-.5c.3-.3.7-.4 1.1-.3l2.2.5 2.6-2.6-.3-1.2 1.1-1.1z"/></svg>
                    </button>
                    <button type="button" class="ins-gr-tb" title="Правка имени и состава" aria-label="правка"
                            @click.stop="editAsm(it.r.asm)">✎</button>
                    <button type="button" class="ins-gr-tb" title="Убрать сборку" aria-label="убрать"
                            @click.stop="removeAsm(it.r.asm)">×</button>
                  </span>
                </span><s v-if="it.r.sub">{{ it.r.sub }}</s></span>
              </td>
              <td v-for="(c, k) in it.cells" :key="k" class="ins-gr-cell">
                <div :style="c.style" :data-t="c.t" :data-v="c.v">{{ c.text }}<i v-if="c.gh" class="ins-gr-gh" :style="c.gh"></i></div>
              </td>
              <td v-for="m in it.metrics" :key="m.field" class="ins-gr-metric" :class="m.cls">
                <button type="button" class="ins-gr-mv" :class="{ 'is-on': m.own }" :title="m.title"
                        @click.stop="onMv(lvKey(it.r), m.field)">
                  <b>{{ m.print }}</b>
                  <em v-if="m.chip" class="ins-gr-mdc" :class="m.chip.cls" :data-t="m.chip.t" :data-v="m.chip.v">{{ m.chip.text }}</em>
                  <i v-if="m.own">{{ m.own.dir > 0 ? '↑' : '↓' }}</i>
                </button>
                <i v-if="m.bar" class="ins-gr-mdb" :class="{ 'is-on': m.bar.on }" :style="{ '--v': m.bar.v + '%', '--m': m.bar.m + '%' }"
                   :data-t="m.bar.t" :data-v="m.bar.d"></i>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-for="h in halos" :key="h.key" class="ins-gr-halo" :class="{ 'is-out': marks.out }" :style="h.style"></div>
      </div>

      <div class="ins-gr-note">{{ note }}</div>

      <details class="ins-gr-raw" @toggle="rawOpen = $event.target.open">
        <summary>таблица данных — те же числа текстом</summary>
        <table v-if="rawOpen" class="ins-gr-t">
          <thead><tr>
            <th class="ins-gr-name">строка</th><th v-for="k in nb" :key="k">блок {{ k }}</th><th>всего</th>
          </tr></thead>
          <tbody>
            <tr v-for="it in view" :key="it.r.key">
              <td class="ins-gr-name">{{ it.r.label }}{{ it.r.sub ? ' — ' + it.r.sub : '' }}</td>
              <td v-for="(v, k) in it.r.hist" :key="k">{{ Math.round(v * it.r.n) }}</td>
              <td>{{ it.r.n }}</td>
            </tr>
          </tbody>
        </table>
      </details>
    </template>
    <!-- Подсказку пишет onTip прямо в DOM: реактивная запись на каждый pointermove
         перерисовывала бы всю решётку. -->
    <div ref="tip" class="ins-gr-tip"><b></b><span></span></div>
  </div>
</template>

<script>
import { buildTree, METRICS, sortLevel, parentBase, levelMedians, assemblyCount } from '@/utils/headGrid';
import { plural, segNameMax, segShortName, segAutoName, newSegId } from '@/utils/segments';
import { phraseColors } from '@/utils/layers';
import HeadGridSegBar from './HeadGridSegBar.vue';

const SES = ['сессия', 'сессии', 'сессий'];

// Сборки и настройки таблицы живут в памяти страницы: секцию решётки сворачивают (компонент
// уничтожается), а собранное и выставленное терять нельзя. В localStorage не кладём: состав
// зависит от среза и периода.
const memory = { assemblies: [], sessions: null, view: null };
// Что переживает «свернуть → показать». Режим набора и черновик не держим: это незаконченное действие.
const KEEP = ['cell', 'open', 'cols', 'diff', 'ref', 'dev', 'sortField', 'sortDir', 'lvSort', 'nameW', 'marks'];

const fix2 = v => (Math.round(v * 100) / 100).toFixed(2).replace('.', ',');
const COLS = Object.keys(METRICS).map(k => ({
  field: k,
  head: METRICS[k].name,                                        // шапка колонки, у оттока с «, %»
  name: k === 'churn' ? 'средний отток' : METRICS[k].name,     // во всех остальных подписях короткое, как имя в П
  help: k === 'churn' ? 'решётка/средний отток' : null,
  fmt: k === 'm' ? fix2 : v => String(Math.round(v)),
  eps: k === 'm' ? 0.005 : 0.5,       // ниже порога отличие считаем нулём
  cls: k === 'churn' ? 'is-out' : '',
  tip: k === 'churn'
    ? 'Средний отток: какая доля уходит за один шаг по блокам, в среднем по шагам. Считается от тех, кто до шага дошёл, а не от всей строки.'
    : '',
}));
// Пояснения кнопок живут в HELP «решётка/<имя>»: плашка HelpPop вместо системного title.
const DEVS = [
  { k: 'up', name: 'к родителю' },
  { k: 'ghost', name: 'силуэт родителя' },
  { k: 'stack', name: 'стопка уровней' },
  { k: 'med', name: 'медиана уровня' },
];
const LV = ['тексты', 'слова', 'фразы'];
// Подписи по ВИДУ строки, а не по номеру уровня: тексты сборки стоят на уровне 1, где у дерева слова.
const KIND1 = { text: 'текст', word: 'слово', phrase: 'фраза', asm: 'сборка' };

// Ключ атома сборки: тот же JSON, что использует assemblyCount.
const ak = p => JSON.stringify([p.text, p.phrase]);
// Клетка красится по ПОТЕРЯННЫМ (100 минус цифра): шкала та же, что в матрице, фиксированные
// шесть ступеней по проценту, а не по максимуму таблицы.
const step = v => (v <= 0 ? 0 : Math.min(100, Math.ceil(v / 20) * 20));
const dvColor = (d, max) => Math.abs(d) < 0.5 ? 'var(--div-mid)'
  : 'color-mix(in oklab, var(--div-' + (d > 0 ? 'pos' : 'neg') + ') ' + (Math.min(1, Math.abs(d) / max) * 90 + 10).toFixed(0) + '%, var(--surface-1))';
const sgn = d => (d > 0 ? '+' : '−');

// Дерево buildTree дополняем тем, что нужно таблице: видом строки, полным текстом (для
// подсказки слова), словом (для цвета фразы) и атомами сборки. Атом = {text, phrase}: строка кладёт в набор все свои фразы.
function decorate(n, tk, tl, wl) {
  const isText = n.level === 0;
  const key0 = isText ? n.key : tk, lab0 = isText ? n.label : tl;
  const word = n.level === 1 ? n.label : wl;
  const kids = n.children.map(c => decorate(c, key0, lab0, word));
  let parts;
  if (n.level === 2) parts = [{ text: key0, phrase: n.label, word: wl }];   // word только ради цвета
  else {
    const seen = new Set();
    parts = [];
    kids.forEach(c => c.parts.forEach(p => { if (!seen.has(p.phrase)) { seen.add(p.phrase); parts.push(p); } }));
  }
  return { ...n, kind: ['text', 'word', 'phrase'][n.level], tl: lab0, word, parts, children: kids };
}

export default {
  name: 'HeadGrid',
  components: { HeadGridSegBar },
  props: {
    slice: { type: Object, required: true },          // {label, phrases: string[] | null, isColumn?}; null: все фразы таблицы
    sessions: { type: Array, required: true },        // К2[] всех сессий периода
    blocks: { type: Array, required: true },
    phraseWords: { type: Object, required: true },    // Object.create(null): фраза → [слово]
  },
  data() {
    // Массив сессий новый на каждую загрузку и стабильный между сворачиваниями секции:
    // другой массив значит другой токен или период, и сборки со старыми ключами текстов бессмысленны.
    if (memory.sessions !== this.sessions) { memory.sessions = this.sessions; memory.assemblies = []; memory.view = null; }
    const kept = memory.view || {};
    return {
      COLS, DEVS,
      cell: 'share',                  // 'share' | 'n'
      open: new Set(),                // ключи раскрытых строк
      cols: { n: false, conv: false, med: false, m: false, churn: false },
      diff: false,                    // разность к опоре
      ref: null,                      // ключ опорной строки
      dev: { up: false, ghost: false, stack: false, med: false },
      sortField: null, sortDir: -1,   // общая сортировка по шапке
      lvSort: {},                     // уровень → {field, dir}
      nameW: null,
      segMode: false, segEdit: null, draft: [],
      assemblies: memory.assemblies,  // [{id, name, auto, pin, atoms}]
      marks: { lift: true, out: false },
      halos: [],
      stopDrag: null,
      rawOpen: false,
      dragged: false,                 // хвост перетаскивания границы не должен сортировать
      dragging: false,                // тяга идёт: полоска красится, курсор на всём body
      ...kept.data,
    };
  },
  computed: {
    nb() { return this.blocks.length; },
    // Цвет фразы как в слоях. Срез-столбец матрицы (слово или сегмент, isColumn) красится как его
    // сцена: phraseColors по сессиям этих фраз, то есть ровно по slSessions.
    phSlice() {
      return this.slice.isColumn ? phraseColors(this.slSessions.map(s => s.phrase)) : null;
    },
    // Срез «все слова» (и «от порога») столбцом не бывает: фразу красим как сцена её слова, по всем сессиям слова
    // за период. Словарь: слово → фраза → цвет.
    phColorMap() {
      const cnt = new Map(), byWord = new Map();
      this.sessions.forEach(s => cnt.set(s.phrase, (cnt.get(s.phrase) || 0) + 1));
      Object.keys(this.phraseWords).forEach(p => this.phraseWords[p].forEach(w => {
        if (!byWord.has(w)) byWord.set(w, []);
        const list = byWord.get(w);
        for (let i = cnt.get(p) || 0; i > 0; i--) list.push(p);
      }));
      const out = new Map();
      byWord.forEach((list, w) => out.set(w, phraseColors(list)));
      return out;
    },
    // Сессии без блоков в решётку не входят (headGrid.buildTree): их число называем в подписи.
    noBlocks() { return this.slSessions.filter(s => !(s.reached >= 0)).length; },
    depthTitle() {
      return ['Только тексты.', 'Под текстом открывается уровень слов: у каждого своё число.', 'Открыть всё дерево до фраз.'];
    },
    slSessions() {
      const ph = this.slice.phrases;
      if (!ph) return this.sessions;
      const set = new Set(ph);
      return this.sessions.filter(s => set.has(s.phrase));
    },
    tree() {
      return buildTree(this.slSessions, this.phraseWords, this.nb).map(t => decorate(t, null, null, null));
    },
    // Весь срез: та же сборка по всем текстам, чтобы числа среза и строк считались одним кодом
    // (сессия под двумя словами один раз).
    sliceRow() {
      const a = assemblyCount(this.tree.map(t => ({ text: t.key, phrase: null })), this.tree, this.nb);
      return { key: '', label: 'весь срез', sub: 'все строки таблицы, ' + a.n + ' ' + plural(a.n, SES), level: -1, ...a };
    },
    // Все пары «текст + фраза» для поиска и «всей таблицы».
    every() {
      const out = [];
      this.tree.forEach(t => t.parts.forEach((p, k) => out.push({ text: t.key, phrase: p.phrase, word: p.word, label: t.label, sub: t.sub, k })));
      return out;
    },
    nameMax() { return segNameMax(this.tree.map(t => ({ word: t.label }))); },
    countByLabel() {
      const by = new Map();
      this.tree.forEach(t => { const s = by.get(t.label) || new Set(); t.parts.forEach(p => s.add(p.phrase)); by.set(t.label, s); });
      const o = Object.create(null);
      by.forEach((s, l) => { o[l] = s.size; });
      return o;
    },
    labelOf() {
      const m = new Map();
      this.tree.forEach(t => m.set(t.key, t.label));
      return m;
    },
    asmNodes() { return this.assemblies.map(a => this.asmNode(a)); },
    allRoots() { return this.tree.concat(this.asmNodes); },
    rows() {
      const free = this.asmNodes.filter(n => !n.asm.pin), pinned = this.asmNodes.filter(n => n.asm.pin);
      // Закреплённые стоят внизу и между собой не сортируются, но их внутренности сортируются:
      // правило уровня одно на всю таблицу.
      const tail = pinned.map(n => ({ ...n, children: this.sortTree(n.children, 1) }));
      return this.flat(this.sortTree(this.tree.concat(free), 0)).concat(this.flat(tail));
    },
    parents() { return parentBase(this.rows); },
    refRow() { return this.ref === null ? null : this.rows.find(r => r.key === this.ref) || null; },
    visCols() { return COLS.filter(c => this.cols[c.field]); },
    depth() { return this.depthOf(this.allRoots); },
    medTab() { return levelMedians(this.tree, COLS.map(c => c.field)); },
    curAsm() { return this.segEdit === null ? null : this.assemblies.find(a => a.id === this.segEdit) || null; },
    curAtoms() { return this.curAsm ? this.curAsm.atoms : this.draft; },
    curKeys() { return new Set(this.curAtoms.map(ak)); },
    curFullName() { return this.curAtoms.length ? segAutoName(this.partsOf(this.curAtoms), this.countByLabel) : ''; },
    curShortName() { return this.autoName(this.curAtoms); },
    legend() {
      const c = this.visCols[0], t = this.medTab;
      if (!c) return {};
      const at = lv => (t[lv] ? c.fmt(t[lv][c.field].med) : '—');
      return {
        sample: METRICS[c.field].print(this.rows[0] || this.sliceRow),
        chip: 'на сколько строка выше (+) или ниже (−) медианы СВОЕГО уровня: в «' + c.name + '» у текстов она ' + at(0)
          + ', у слов ' + at(1) + ', у фраз ' + at(2) + (this.visCols.length > 1 ? '; у остальных колонок свои, они написаны в их шапках' : ''),
      };
    },
    view() {
      const rows = this.rows, par = this.parents, sl = this.sliceRow, share = this.cell === 'share';
      const ref = this.refRow, base = ref ? ref.hist : sl.hist;
      const upMode = this.dev.up && !this.diff;
      // Единицы клетки задаёт переключатель и в режимах разности тоже. База хранится долями и
      // пересчитывается под размер каждой строки, иначе в «штуках» сравнивались бы размеры, а не форма.
      const inUnits = r => r.hist.map(v => (share ? v * 100 : Math.round(v * r.n)));
      const baseOf = (r, b) => b.map(v => (share ? v * 100 : v * r.n));
      let maxDiff = 0.0001, maxUp = 0.0001;
      const pre = rows.map((r, i) => {
        const p = par[i], ph = p ? p.hist : sl.hist, u = inUnits(r), bd = baseOf(r, base), bp = baseOf(r, ph);
        u.forEach((v, k) => { maxDiff = Math.max(maxDiff, Math.abs(v - bd[k])); maxUp = Math.max(maxUp, Math.abs(v - bp[k])); });
        return { r, p, ph, u, bd, bp };
      });
      const tab = this.dev.med ? this.medTab : null;
      const refLv = ref && ref.kind !== 'asm' && !ref.asmKey ? ref.level : null;
      const unit = dv => share ? 'п.п.' : plural(dv, SES);
      return pre.map(({ r, p, ph, u, bd, bp }) => {
        const isAsm = r.kind === 'asm', root = isAsm ? r.key : (r.asmKey || null);
        const cls = ['ins-gr-lv' + r.level];
        if (r.level > 0) cls.push('is-sub');
        if (this.ref === r.key) cls.push('is-ref');
        if (root) cls.push(isAsm ? 'is-asm' : 'is-asmch', this.markCls);
        if (this.segMode) {
          cls.push('is-pickon');
          if (!isAsm && r.parts && r.parts.length && r.parts.every(x => this.curKeys.has(ak(x)))) cls.push('is-picked');
        }
        const parName = p ? p.label : 'весь срез';
        const cells = r.hist.map((v, k) => {
          const val = u[k], dv = val - bd[k], dp = val - bp[k], shift = (v - ph[k]) * 100;
          let style, text;
          if (upMode) {
            style = { background: dvColor(dp, maxUp) };
            text = Math.abs(dp) < 0.5 ? '' : sgn(dp) + Math.round(Math.abs(dp));
          } else if (this.diff) {
            style = { background: dvColor(dv, maxDiff) };
            text = Math.abs(dv) < 0.5 ? '' : sgn(dv) + Math.round(Math.abs(dv));
          } else {
            const st = step(100 - v * 100);
            style = { background: 'var(--seq-' + st + ')', color: 'var(--seq-ink-' + st + ')' };
            text = val <= 0 ? '—' : Math.round(val);
          }
          // Силуэт: кусок от своей доли до родительской; меньше двух пунктов это шум округления.
          const own = Math.max(0, Math.min(100, v * 100)), pr = Math.max(0, Math.min(100, ph[k] * 100));
          const gh = this.dev.ghost ? {
            '--own': own.toFixed(1) + '%', '--from': Math.min(own, pr).toFixed(1) + '%',
            '--len': Math.abs(own - pr).toFixed(1) + '%',
            '--ghc': Math.abs(shift) < 2 ? 'transparent' : (shift > 0 ? 'var(--div-pos)' : 'var(--div-neg)'),
          } : null;
          return {
            style, text, gh,
            t: r.label + ' · блок ' + (k + 1),
            v: 'дошли ' + Math.round(v * r.n) + ' из ' + r.n + ' · ' + Math.round(v * 100) + ' % · потеряно '
              + (100 - Math.round(v * 100)) + ' % (по нему и цвет)'
              + (this.diff ? ' · к опоре ' + (dv > 0 ? '+' : '') + Math.round(dv) + ' ' + unit(Math.round(dv)) : '')
              + (upMode || this.dev.ghost
                ? ' · «' + parName + '» здесь ' + Math.round(ph[k] * 100) + ' % · смещение '
                  + (shift > 0 ? '+' : shift < 0 ? '−' : '') + Math.round(Math.abs(shift)) + ' п.п.' : ''),
          };
        });
        const metrics = this.visCols.map(c => {
          // Медиана это свойство уровня ДАННЫХ: у сборки и её внутренностей уровня нет.
          const mu = tab && !root && tab[r.level] ? tab[r.level][c.field] : null;
          const zn = METRICS[c.field].val(r), d = mu ? zn - mu.med : 0;
          const share100 = x => (mu && mu.max > 0 ? Math.max(0, Math.min(100, x / mu.max * 100)).toFixed(1) : '0.0');
          const lk = this.lvKey(r), ln = this.lvName(lk);
          const own = this.lvSort[lk] && this.lvSort[lk].field === c.field ? this.lvSort[lk] : null;
          const zero = Math.abs(d) < c.eps;
          return {
            field: c.field, cls: c.cls, print: METRICS[c.field].print(r), own,
            title: own
              ? 'Сейчас ' + ln + ' отсортированы по «' + c.name + '» ' + (own.dir > 0 ? 'по возрастанию' : 'по убыванию') + '. Клик: ' + (own.dir > 0 ? 'снять сортировку' : 'перевернуть') + '.'
              : 'Сортировать ' + ln + ' по показателю «' + c.name + '» по убыванию. Ещё клик: по возрастанию, третий: снять. Другие уровни останутся как есть.',
            chip: mu ? {
              cls: zero ? 'is-z' : d > 0 ? 'is-p' : 'is-m', text: zero ? '±0' : sgn(d) + c.fmt(Math.abs(d)),
              t: 'отличие от медианы своего уровня',
              v: (zero ? 'Строка идёт вровень с медианой' : 'Строка ' + (d > 0 ? 'выше' : 'ниже') + ' медианы на ' + c.fmt(Math.abs(d)))
                + ': медиана «' + LV[r.level] + '» по показателю «' + c.name + '» ' + c.fmt(mu.med) + ', посчитана по всем ' + mu.n + ' строкам этого уровня.',
            } : null,
            bar: mu ? {
              v: share100(zn), m: share100(mu.med), on: refLv === r.level,
              t: 'медиана уровня «' + LV[r.level] + '»',
              d: c.name + ': ' + c.fmt(mu.med) + ' по всем ' + mu.n + ' строкам этого уровня в дереве, включая свёрнутые. Заливка полоски это значение строки, вся дорожка от нуля до наибольшего на уровне (' + c.fmt(mu.max) + ').',
            } : null,
          };
        });
        return { r, cls, root, cells, metrics };
      });
    },
    markCls() {
      return this.marks.lift || this.marks.out ? (this.marks.lift ? 'mk-lift' : '') + (this.marks.out ? ' mk-out' : '') : 'mk-plain';
    },
    stack() {
      if (!this.dev.stack) return null;
      const rows = this.rows, par = this.parents;
      const chain = [];
      for (let i = this.refRow ? rows.indexOf(this.refRow) : -1; i >= 0;) {
        chain.unshift(rows[i]);
        i = par[i] ? rows.indexOf(par[i]) : -1;
      }
      chain.unshift(this.sliceRow);
      return chain.map((r, j) => {
        const prev = j ? chain[j - 1] : null;
        return {
          r, tag: j === 0 ? 'срез' : KIND1[r.kind],
          cells: r.hist.map((v, k) => {
            const st = step(100 - v * 100), d = prev ? (v - prev.hist[k]) * 100 : 0;
            return {
              style: { background: 'var(--seq-' + st + ')', color: 'var(--seq-ink-' + st + ')' }, text: Math.round(v * 100),
              d: prev ? { cls: Math.abs(d) < 0.5 ? 'is-z' : d > 0 ? 'is-p' : 'is-m', text: Math.abs(d) < 0.5 ? '±0' : sgn(d) + Math.round(Math.abs(d)) } : null,
            };
          }),
          // Числовые колонки со смещением к ступени выше: расхождение уровней видно и по ним.
          metrics: this.visCols.map(c => {
            const zn = METRICS[c.field].val(r), d = prev ? zn - METRICS[c.field].val(prev) : 0;
            return {
              field: c.field, cls: c.cls, print: METRICS[c.field].print(r),
              d: prev ? { cls: Math.abs(d) < c.eps ? 'is-z' : d > 0 ? 'is-p' : 'is-m', text: Math.abs(d) < c.eps ? '±0' : sgn(d) + c.fmt(Math.abs(d)) } : null,
            };
          }),
        };
      });
    },
    sliceKey() {
      return this.slice.label + '|' + (this.slice.phrases ? this.slice.phrases.join('\u0001') : '*');
    },
    note() {
      const cf = this.cell === 'share', n = this.sliceRow.n;
      const t = [];
      if (this.dev.up && !this.diff) {
        t.push('Клетка: насколько ' + (cf ? 'доля' : 'число сессий') + ' строки отличается от строки уровнем выше. Слово сравнивается со своим текстом, фраза со своим словом, а текст сравнивать не с чем, кроме всего среза, с ним и сравнивается ('
          + (cf ? 'в процентных пунктах' : 'в сессиях') + '). Красное: ветка держит лучше родителя, синее: хуже, серое: так же. У каждой строки своя база, поэтому видно не где вообще много, а где ветка отходит от собственного уровня выше.');
      } else if (this.diff) {
        t.push('Клетка: насколько ' + (cf ? 'доля' : 'число сессий') + ' отличается от ' + (this.refRow ? 'опорной строки' : 'всего среза')
          + ', ' + (cf ? 'в процентных пунктах' : 'в сессиях') + '. Красное больше, синее меньше, серое поровну.'
          + (cf ? '' : ' Сравнение идёт с формой ' + (this.refRow ? 'опоры' : 'среза') + ', пересчитанной на размер строки: иначе разница показывала бы разницу числа сессий, а не формы.'));
      } else {
        t.push('Клетка: доля сессий строки, ДОШЕДШИХ до этого блока, то есть воронка. В каждом следующем блоке число может быть только меньше или таким же, вырасти оно не может. Первый блок всегда 100 %: до него доходят все.'
          + ' Цифра и цвет говорят о разном: цифра это сколько ДОШЛО, цвет это сколько уже ПОТЕРЯНО (100 минус цифра), поэтому путь идёт слева направо и к хвосту темнеет. Шкала та же, что в матрице: шесть ступеней, прочерк на нуле. В режиме «штуки» цифра показывает сессии, цвет по-прежнему считается в процентах. Клик по строке делает её опорой. Клик по шапке сортирует: по названию алфавит, по номеру блока доля дошедших, по числовой колонке её показатель. Третий клик снимает сортировку.');
      }
      t.push('Четыре кнопки «отклонение» это четыре способа увидеть, где статистика уровней расходится: «к родителю» перекрашивает всю таблицу по расхождению с уровнем выше, «силуэт родителя» оставляет обычную раскраску и кладёт на клетку полоску, «стопка уровней» разворачивает одну ветку отдельной панелью сверху, «медиана уровня» наносит на строки медиану их собственного уровня. Они независимы, можно включить любую или все сразу.'
        + (this.dev.ghost ? ' Силуэт включён: на полоске внизу клетки залита своя доля, а цветной кусок тянется от неё до доли родителя; синий: ветка просела сильнее родителя, красный: держит лучше, смещение меньше двух пунктов не красится.' : '')
        + (this.dev.up && this.diff ? ' Сейчас «к родителю» отключено разностью к опоре: у клетки может быть только одна база. Снимите опору, и вернётся родитель.' : '')
        + (this.dev.stack ? ' Стопка показывает ветку опорной строки.' : ''));
      if (this.dev.med && this.visCols.length) {
        const c = this.visCols[0], keys = Object.keys(this.medTab).map(Number).sort();
        const sums = keys.map(k => Math.round(this.medTab[k][c.field].sum));
        const rest = this.visCols.slice(1).map(o => '«' + o.name + '» ' + keys.map(k => o.fmt(this.medTab[k][o.field].med)).join(' / ')).join(', ');
        t.push('Медиана уровня включена. У каждого уровня своя медиана показателя «' + c.name + '», посчитанная по ВСЕМ строкам уровня в дереве, а не только по раскрытым: '
          + keys.map(k => LV[k] + ' ' + c.fmt(this.medTab[k][c.field].med) + ' (строк ' + this.medTab[k][c.field].n + ')').join(', ') + '.'
          + (rest ? ' У остальных колонок свои, считанные так же (тексты / слова / фразы): ' + rest + '.' : '')
          + ' Полоска под числом это размах уровня: заливка значение строки, засечка медиана уровня, и она стоит на одном месте у всех соседей; чип рядом с числом показывает, на сколько строка от неё отличается.'
          + (!this.refRow ? ' Кликните по строке, и засечки её уровня выделятся.'
            : this.refRow.kind === 'asm' || this.refRow.asmKey ? ' Опорная строка лежит в сборке, а у сборок медианы уровня нет, поэтому засечки не выделены.'
              : ' Опорная строка на уровне «' + LV[this.refRow.level] + '», засечки этого уровня выделены.')
          + ' Медианы уровней совпадать не обязаны и обычно не совпадают: медиана считается ПО СТРОКАМ, а строк на уровнях разное число и вес у них разный, '
          + keys.map(k => this.medTab[k][c.field].n + ' ' + LV[k]).join(' против ') + '.'
          + (keys.length > 1 && new Set(sums).size === 1 ? ' Совпадает сумма (' + sums[0] + '): уровни делят одни и те же сессии без остатка. Совпадает и среднее, взвешенное по сессиям, а медиана и простое среднее по строкам нет.'
            : ' Складывать этот показатель по строкам смысла нет, уровни сравниваются только по форме.')
          + ' Сборки в медиану не входят: их сессии уже посчитаны в строках, из которых они собраны.');
      }
      t.push('Границу колонки с заголовками можно потянуть за правый край её шапки: текст переносится по словам и виден целиком, двойной клик по границе возвращает ширину по содержимому.');
      t.push('«Собрать строку» складывает свою строку из заголовков и отдельных фраз: кликай по строкам таблицы или ищи фразу в поиске. Сессия, попавшая в сборку из двух слов, считается один раз. Сборка помечена пунктиром, булавка держит её внизу вне сортировок, без булавки она сортируется наравне со строками. Раскрывается она теми же треугольником и кнопками глубины: под ней заголовки, из которых собрана, под заголовком его фразы. Общая сессия достаётся тому заголовку, который выиграл при дедупе, поэтому уровни складываются в сборку ровно. Новая сборка появляется с уже применёнными настройками таблицы: раскрыта на ту же глубину, отсортирована теми же поуровневыми правилами, в тех же единицах клетки и с той же отметкой.'
        + (this.assemblies.length ? ' Сейчас сборок ' + this.assemblies.length + '.' : ''));
      t.push('Срез задаёт матрица выше: сейчас ' + this.slice.label + ', ' + n + ' ' + plural(n, ['сессия', 'сессии', 'сессий']) + '.'
        + (this.noBlocks ? ' Сессий среза, не дошедших ни до одного блока, ещё ' + this.noBlocks + ': в решётку они не входят вовсе, ни в строки, ни в весь срез, и их конверсии тоже не считаются.' : '')
        + ' Кликните другой столбец в матрице, и решётка пересчитается, опора при этом сбросится, а сборки останутся. Три уровня: текст, слово, фраза. Текст это заголовок с подзаголовком, который видел посетитель, слово и фраза это то, с чем он пришёл. «нет данных» это сессии без записанного показа, «без подмены» это сессии, где показ известен, но заголовка нет. Треугольник раскрывает ветку на уровень, с Alt или Shift сразу до конца; кнопки «раскрыть» задают глубину всему дереву.');
      t.push('Клик по числу в числовой колонке сортирует только свой уровень: число на тексте переставит тексты, на слове слова, на фразе фразы, а остальные уровни не сдвинутся.'
        + (Object.keys(this.lvSort).length ? ' Сейчас так отсортированы: ' + Object.keys(this.lvSort).map(lv => this.lvName(lv) + ' по «' + COLS.find(c => c.field === this.lvSort[lv].field).name + '» ' + (this.lvSort[lv].dir > 0 ? 'по возрастанию' : 'по убыванию')).join(', ') + '. Третий клик по числу снимает.' : '')
        + (this.visCols.length > 1 ? ' Числовых колонок сейчас ' + this.visCols.length + ', и сортируются они по отдельности: клик по числу в любой из них сортирует свой уровень именно по ней.'
          : this.visCols.length === 1 ? ' Показана одна числовая колонка, «' + this.visCols[0].name + '». Остальные включаются кнопками «колонки», и их можно включить сколько угодно сразу.'
          : ' Числовые колонки все спрятаны: остались имя и тепловая карта по блокам, кнопки «колонки» их вернут.'));
      if (this.cols.churn) t.push('Средний отток: какая доля уходит за один шаг по блокам, в среднем по шагам, от тех, кто до шага дошёл, а не от всей строки. 60 % значит «на каждом шаге теряется примерно шестьдесят процентов оставшихся», а не «строка потеряла 60 % целиком». Разность соседних долей брать нельзя: она телескопируется, её среднее всегда равно (100 минус доля в последнем блоке) / ' + Math.max(1, this.nb - 1) + ' и о форме воронки не говорит ничего, у ровного спуска и у обрыва в первом же блоке оно одинаковое.');
      if (this.cols.conv) t.push('Конверсий во всём срезе ' + this.sliceRow.conv + ' на ' + n + ' ' + plural(n, SES) + ', поэтому у большинства строк здесь прочерк.'
        + (this.noBlocks ? ' Конверсии сессий, не дошедших ни до одного блока, сюда не входят.' : ''));
      return t.join(' ');
    },
  },
  watch: {
    // Свернули ветку с опорой: строки нет в rows, и клетки молча сравнивались бы со срезом.
    rows() {
      if (this.ref !== null && !this.rows.some(r => r.key === this.ref)) this.unref();
    },
    // Опора относится к строке старого среза: в новом её может не быть. Сборки не трогаем,
    // их состав задан парами «текст + фраза» и честно пересчитается.
    sliceKey() {
      this.ref = null;
      this.diff = false;
    },
  },
  updated() { this.measure(); },
  created() {
    // Пока секция была свёрнута, матрица могла сменить срез: опора старого среза не годится.
    if (memory.view && memory.view.sliceKey !== this.sliceKey) this.unref();
  },
  mounted() {
    this.measure();
    // Секцию прячут v-show на время слоёв: resize в скрытой решётке мерить нечего, а при показе
    // размер корня меняется с нуля, и ResizeObserver пересчитает ореолы.
    this.ro = new ResizeObserver(() => this.measure());
    this.ro.observe(this.$el);
  },
  beforeUnmount() {
    const data = {};
    KEEP.forEach(k => { data[k] = this[k]; });
    memory.view = { data, sliceKey: this.sliceKey };
    this.ro.disconnect();
    this.stopDrag && this.stopDrag();
    document.body.classList.remove('ins-gr-rzing');
  },
  methods: {
    plural,
    phColorOf(r) {
      if (this.phSlice) return this.phSlice[r.label] || 'var(--ph0)';
      const w = r.word || (this.phraseWords[r.label] || [])[0];   // слово неизвестно (найдено поиском): первое
      const m = w != null && this.phColorMap.get(w);
      return (m && m[r.label]) || 'var(--ph0)';
    },
    phColorAny(p) { return this.phColorOf({ label: p.phrase, word: p.word }); },
    asmFullName(a) { return a.atoms.length ? segAutoName(this.partsOf(a.atoms), this.countByLabel) : a.name; },
    partsOf(atoms) {
      return atoms.map(a => ({ word: this.labelOf.get(a.text) || a.text, phrase: a.phrase }));
    },
    // «вся таблица» только когда покрыты все пары «текст + фраза» среза (П сегКоротко), а не все фразы:
    // фраза бывает под несколькими текстами, и два текста часто собирают все фразы. Infinity гасит матричное «таблица».
    autoName(atoms) {
      if (!atoms.length) return '';
      const keys = new Set(atoms.map(ak));
      if (this.every.every(p => keys.has(ak(p)))) return 'вся таблица';
      return segShortName(this.partsOf(atoms), this.nameMax, this.countByLabel, Infinity);
    },
    // Узел таблицы из сборки: сборка (0) → тексты (1) → фразы (2). Слов в сборке нет:
    // общая сессия достаётся тексту-победителю при дедупе, и уровни складываются ровно.
    asmNode(a) {
      const c = assemblyCount(a.atoms.map(x => ({ text: x.text, phrase: x.phrase })), this.tree, this.nb);
      const key = 'asm:' + a.id, wordOf = new Map(a.atoms.map(x => [ak(x), x.word]));
      const n = a.atoms.length;
      const children = c.byText.map(t => ({
        ...t, key: key + '/' + t.key, level: 1, kind: 'text', asmKey: key, tl: t.label,
        // parts: клик по ребёнку в режиме набора кладёт его в состав (текст: все фразы узла дерева)
        parts: (this.tree.find(x => x.key === t.key) || { parts: [] }).parts,
        children: t.children.map(p => {
          const word = wordOf.get(ak({ text: t.key, phrase: p.label }));
          return { ...p, key: key + '/' + p.key, level: 2, kind: 'phrase', asmKey: key, children: [], word,
            parts: [{ text: t.key, phrase: p.label, word }] };
        }),
      }));
      return {
        key, level: 0, kind: 'asm', asm: a, label: a.name, children,
        sub: 'сборка из ' + n + ' ' + plural(n, ['части', 'частей', 'частей']) + (c.dups ? ' · ' + c.dups + ' общих учтены один раз' : ''),
        n: c.n, hist: c.hist, med: c.med, m: c.m, conv: c.conv,
      };
    },
    // Сортировка по уровням: дети остаются под своим родителем. Как в прототипе, правило уровня
    // одно на всю таблицу: тексты сборки идут по правилу слов (уровень 1), её фразы по правилу фраз.
    sortTree(list, lv) {
      const own = this.lvSort[lv];
      const rule = own || (this.sortField ? { field: this.sortField, dir: this.sortDir } : null);
      return sortLevel(list, rule).map(r => (r.children.length ? { ...r, children: this.sortTree(r.children, lv + 1) } : r));
    },
    lvKey(r) { return r.level; },
    lvName(k) { return +k === 1 && this.assemblies.length ? 'слова и тексты сборок' : LV[k]; },
    flat(list, out = []) {
      list.forEach(r => {
        out.push(r);
        if (r.children.length && this.open.has(r.key)) this.flat(r.children, out);
      });
      return out;
    },
    // Насколько глубоко раскрыто по факту: 1 ничего, 2 первый уровень, 3 и второй.
    depthOf(list) {
      let d = 1;
      const walk = (l, lv) => l.forEach(r => {
        if (!r.children.length || !this.open.has(r.key)) return;
        d = Math.max(d, lv + 2);
        walk(r.children, lv + 1);
      });
      walk(list, 0);
      return d;
    },
    openTo(node, d) {
      const walk = (r, lv) => {
        if (lv >= d - 1 || !r.children.length) return;
        this.open.add(r.key);
        r.children.forEach(x => walk(x, lv + 1));
      };
      walk(node, 0);
    },
    setDepth(d) {
      this.open = new Set();
      this.allRoots.forEach(r => this.openTo(r, d));
    },
    branch(r, fn) {
      if (r.children.length) fn(r.key);
      r.children.forEach(c => this.branch(c, fn));
    },
    toggleRow(r, ev) {
      if (this.open.has(r.key)) this.branch(r, k => this.open.delete(k));
      else if (ev.altKey || ev.shiftKey) this.branch(r, k => this.open.add(k));
      else this.open.add(r.key);
    },
    arrow(f) { return this.sortField === f ? (this.sortDir > 0 ? ' ↑' : ' ↓') : ''; },
    onHead(f) {
      if (this.dragged) return;                // хвост перетаскивания границы, не сортировка
      // Повторный клик переворачивает порядок, третий снимает сортировку.
      if (this.sortField !== f) { this.sortField = f; this.sortDir = f === 'name' ? 1 : -1; }
      else if ((f === 'name' && this.sortDir === 1) || (f !== 'name' && this.sortDir === -1)) this.sortDir = -this.sortDir;
      else { this.sortField = null; this.sortDir = -1; }
      this.lvSort = {};                         // шапка задаёт порядок всем уровням сразу
    },
    onMv(lv, field) {
      const c = this.lvSort[lv];
      // Другая колонка начинает свою сортировку заново, направление соседней не подхватывает.
      if (!c || c.field !== field) this.lvSort[lv] = { field, dir: -1 };
      else if (c.dir === -1) c.dir = 1;
      else delete this.lvSort[lv];
    },
    toggleCol(f) {
      this.cols[f] = !this.cols[f];
      if (this.cols[f]) return;
      // Спрятанная колонка не должна втихую сортировать таблицу.
      if (this.sortField === f) { this.sortField = null; this.sortDir = -1; }
      Object.keys(this.lvSort).forEach(lv => { if (this.lvSort[lv].field === f) delete this.lvSort[lv]; });
    },
    toggleDiff() {
      this.diff = !this.diff;
      if (this.diff) this.dev.up = false;       // у клетки одна база
    },
    unref() {
      this.ref = null;
      this.diff = false;
    },
    toggleDev(k) {
      this.dev[k] = !this.dev[k];
      if (k === 'up' && this.dev.up) this.diff = false;
    },
    onRow(r) {
      if (this.segMode) {
        if (r.kind === 'asm') this.segEdit = r.asm.id;
        else this.togglePick(r);
        return;
      }
      if (this.ref === r.key) this.unref();
      else { this.ref = r.key; this.diff = !this.dev.up; }
    },
    rowTitle(r) {
      if (this.segMode) return r.kind === 'asm' ? 'Клик: править сборку' : 'Набор сборки: клик добавит эту строку в состав';
      return this.ref === r.key ? 'Это опора: остальные строки показывают разницу с ней. Клик: снять опору и вернуть воронку'
        : 'Клик: сделать опорой, остальные строки покажут, насколько они отличаются от этой, а не свою воронку';
    },
    ttlTip(r) {
      if (r.kind === 'word') return 'Уникальный заголовок целиком: «' + r.tl + '». В строке написано только слово, текст стоит строкой выше, и повторять его здесь было бы дублем.';
      if (r.kind === 'text' && !r.asmKey) return 'Текст без слова: строка считает все слова среза сразу. Слово написано строкой ниже, там у каждого своё число.';
      return '';
    },
    // --- сборки ---
    toggleSegMode() {
      this.segMode = !this.segMode;
      if (!this.segMode) { this.segEdit = null; this.draft = []; }
    },
    togglePick(r) {
      if (!r.parts || !r.parts.length) return;
      const have = this.curKeys, inside = r.parts.every(p => have.has(ak(p)));
      const drop = new Set(r.parts.map(ak));
      this.setAtoms(inside ? this.curAtoms.filter(p => !drop.has(ak(p))) : this.curAtoms.concat(r.parts.filter(p => !have.has(ak(p)))));
    },
    setAtoms(list) {
      const a = this.curAsm;
      if (!a) { this.draft = list; return; }
      // Правка добавляет и убирает ветки: раскрытие доложим до прежней глубины.
      const d = this.depthOf([this.asmNode(a)]);
      a.atoms = list;
      if (a.auto) a.name = this.autoName(list) || a.name;
      if (d > 1) this.openTo(this.asmNode(a), d);
    },
    onNewAsm() { this.segEdit = null; this.draft = []; },
    onRename(v) {
      const a = this.curAsm;
      if (!a) return;
      // Пустое поле: снова автоимя; своё уже не переписывается.
      a.auto = !v;
      a.name = v || this.autoName(a.atoms) || 'сборка';
    },
    onCommit(name) {
      if (!this.draft.length) return;
      // Глубину снимаем ДО добавления: новая строка открывается так же, как остальная таблица.
      const d = this.depth;
      const a = { id: newSegId(), name: name.slice(0, this.nameMax) || this.autoName(this.draft) || 'сборка', auto: !name, pin: true, atoms: this.draft.slice() };
      this.assemblies.push(a);
      this.openTo(this.asmNode(a), d);
      this.draft = [];
      this.segEdit = null;
      this.segMode = false;                     // набор кончился; включённый перехватывал бы клики по строкам
    },
    editAsm(a) {
      this.segMode = true;
      this.segEdit = a.id;
    },
    removeAsm(a) {
      const i = this.assemblies.findIndex(x => x.id === a.id);
      if (i >= 0) this.assemblies.splice(i, 1);
      if (this.ref !== null && this.ref.startsWith('asm:' + a.id)) this.unref();
      if (this.segEdit === a.id) this.segEdit = null;
    },
    // --- ореол сборки: один слой на строку под клетками, у клеток свои скругления и зазоры ---
    measure() {
      const wrap = this.$refs.wrap;
      let next = [];
      if (wrap && this.assemblies.length && (this.marks.lift || this.marks.out)) {
        const w = wrap.getBoundingClientRect(), by = new Map();
        if (!w.width) return;                   // скрыта: прямоугольники нулевые, старые ореолы не трогаем
        wrap.querySelectorAll('tbody tr[data-root]').forEach(tr => {
          const k = tr.dataset.root;
          if (!by.has(k)) by.set(k, []);
          by.get(k).push(tr);
        });
        by.forEach((trs, key) => {
          const a = trs[0].getBoundingClientRect(), b = trs[trs.length - 1].getBoundingClientRect();
          next.push({ key, style: { left: (a.left - w.left + wrap.scrollLeft + (this.marks.out ? -6 : 0)) + 'px', top: (a.top - w.top + wrap.scrollTop) + 'px', width: a.width + 'px', height: (b.bottom - a.top) + 'px' } });
        });
      }
      // Сравниваем перед записью: measure зовётся из updated, лишняя запись зациклила бы отрисовку.
      if (JSON.stringify(next) !== JSON.stringify(this.halos)) this.halos = next;
    },
    // --- ширина колонки имён ---
    startDrag(ev) {
      if (ev.button) return;
      ev.preventDefault();
      ev.stopPropagation();
      const th = ev.currentTarget.closest('th'), x0 = ev.clientX, w0 = th.getBoundingClientRect().width;
      this.dragged = false;
      this.dragging = true;
      document.body.classList.add('ins-gr-rzing');
      const mv = e => {
        this.dragged = true;
        this.nameW = Math.max(140, Math.round(w0 + e.clientX - x0));   // ниже слова рвутся посередине
      };
      this.stopDrag = () => {
        document.removeEventListener('pointermove', mv);
        document.removeEventListener('pointerup', this.stopDrag);
        this.stopDrag = null;
        this.dragging = false;
        document.body.classList.remove('ins-gr-rzing');
        setTimeout(() => { this.dragged = false; }, 0);      // click прилетает в том же кадре, что pointerup
      };
      document.addEventListener('pointermove', mv);
      document.addEventListener('pointerup', this.stopDrag);
    },
    // --- подсказка: одна на всю решётку, текст из data-t / data-v, едет за курсором ---
    onTip(e) {
      const el = e.target.closest && e.target.closest('[data-t]');
      if (!el || !this.$el.contains(el)) { this.hideTip(); return; }
      const box = this.$refs.tip;
      if (box.dataset.for !== el.dataset.t + el.dataset.v) {
        box.dataset.for = el.dataset.t + el.dataset.v;
        box.firstChild.textContent = el.dataset.t;
        box.lastChild.textContent = el.dataset.v;
      }
      box.classList.add('is-on');               // opacity 0 → 1 за 0,1 с, гаснет так же (П:1484)
      const w = box.offsetWidth, h = box.offsetHeight, M = 8;
      let x = e.clientX + 14, y = e.clientY + 16;
      if (x + w > window.innerWidth - M) x = Math.max(M, e.clientX - 14 - w);
      if (y + h > window.innerHeight - M) y = Math.max(M, e.clientY - 16 - h);
      box.style.left = x + 'px';
      box.style.top = y + 'px';
    },
    hideTip() {
      const box = this.$refs.tip;
      if (box) { box.classList.remove('is-on'); box.dataset.for = ''; }
    },
    medHead(c) {
      const t = this.medTab;
      return Object.keys(t).map(Number).sort().map(lv => LV[lv] + ' ' + c.fmt(t[lv][c.field].med)).join(' · ');
    },
  },
};
</script>

<style>
/* Не scoped: классы с префиксом ins-gr-, а bootstrap перекрывает короткие имена даже при scoped. */
/* отступ от описания секции над решёткой и под тулбаром как в прототипе (.phead 18, .g-bar 16) */
.ins-root .ins-gr{position:relative; margin-top:18px;}
.ins-root .ins-gr-bar{display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:16px;}
.ins-root .ins-gr-bar label{font-size:12px; color:var(--text-muted);}
/* подпись и её кнопки одной группой, в П между ними просто пробел (~4px) */
.ins-root .ins-gr-dev{display:inline-flex; align-items:center; gap:4px;}
/* сегментированный переключатель: общая рамка, кнопки без скруглений между собой */
.ins-root .ins-gr-seg{display:inline-flex; border:1px solid var(--grid); border-radius:8px; overflow:hidden;}
.ins-root .ins-gr-seg > .tgl{border:0; border-radius:0; border-right:1px solid var(--grid); padding:5px 10px;}
.ins-root .ins-gr-seg > .tgl:last-child{border-right:0;}
/* одиночные кнопки и кнопки сегментов как .g-b прототипа: радиус 8, primary, без жирного у нажатых */
.ins-root .ins-gr-bar .tgl{border-radius:8px; padding:5px 10px; color:var(--text-primary); border-color:var(--grid);}
.ins-root .ins-gr-bar .tgl[aria-pressed="true"]{color:#fff; font-weight:400;}
.ins-root .ins-gr-bar .ins-gr-seg > .tgl{border-radius:0;}
.ins-root .ins-gr-phead{display:flex; align-items:baseline; gap:12px; flex-wrap:wrap; margin-bottom:12px;}
.ins-root .ins-gr-tag{font-size:11px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color:var(--text-muted);
  border:1px solid var(--grid); border-radius:999px; padding:2px 9px;}
/* relative: подпись ложится поверх нижнего запаса обёртки, иначе первую строку не выделить */
.ins-root .ins-gr-note{position:relative; color:var(--text-muted); font-size:12px; margin-top:10px;}

/* padding под ореол сборки и сдвиг «выдвинута» (−6px, тень до 20px), иначе overflow их срезает */
.ins-root .ins-gr-wrap{position:relative; overflow-x:auto; padding:10px 24px 28px; margin:-10px -24px -28px;}
.ins-root .ins-gr-t{border-collapse:separate; border-spacing:0; width:100%; font-size:12.5px;}
.ins-root .ins-gr-t th, .ins-root .ins-gr-t td{padding:4px 6px; text-align:center; font-variant-numeric:tabular-nums;}
/* Колонка имён по умолчанию подгоняется под самый длинный текст; с заданной шириной включается перенос. */
.ins-root .ins-gr-t th.ins-gr-name, .ins-root .ins-gr-t td.ins-gr-name{text-align:left; width:1%; white-space:nowrap; padding-right:14px;}
.ins-root .ins-gr-t.is-fixname th.ins-gr-name, .ins-root .ins-gr-t.is-fixname td.ins-gr-name{
  width:var(--namew); min-width:var(--namew); max-width:var(--namew); white-space:normal; overflow-wrap:break-word;}
.ins-root .ins-gr-t th.ins-gr-name{position:relative;}
.ins-root .ins-gr-rz{position:absolute; top:0; right:0; width:9px; height:100%; cursor:col-resize; display:block;}
.ins-root .ins-gr-rz:before{content:""; position:absolute; top:3px; bottom:3px; right:3px; width:2px; background:var(--grid); border-radius:1px;}
.ins-root .ins-gr-rz:hover:before, .ins-root .ins-gr-rz.is-drag:before{background:var(--accent);}
/* на время тяги курсор и запрет выделения на всю страницу: курсор уходит с полоски */
body.ins-gr-rzing, body.ins-gr-rzing *{cursor:col-resize !important; user-select:none !important;}
.ins-root .ins-gr-t th.ins-gr-b{cursor:pointer; user-select:none; color:var(--text-secondary); font-weight:600;}
.ins-root .ins-gr-t th.ins-gr-b:hover{color:var(--text-primary);}
.ins-root .ins-gr-t th.ins-gr-b.is-sorted{color:var(--text-primary); text-decoration:underline;}
.ins-root .ins-gr-t th.ins-gr-metric, .ins-root .ins-gr-t td.ins-gr-metric{border-left:1px solid var(--grid); padding-left:12px; min-width:96px; position:relative;}
.ins-root .ins-gr-t th.ins-gr-metric.is-out{white-space:nowrap;}
.ins-root .ins-gr-t th.ins-gr-metric.is-out, .ins-root .ins-gr-t td.ins-gr-metric.is-out{min-width:88px;}
.ins-root .ins-gr-mdh{display:block; font-weight:400; font-size:10.5px; color:var(--text-muted); margin-top:2px; white-space:nowrap;}
.ins-root .ins-gr-mv{font:inherit; font-variant-numeric:tabular-nums; color:inherit; border:0; background:none; padding:1px 6px;
  border-radius:6px; cursor:pointer; line-height:1.4;}
.ins-root .ins-gr-mv b{font-weight:600;}
.ins-root .ins-gr-mv i{font-style:normal; font-size:10px; margin-left:3px; opacity:.85;}
.ins-root .ins-gr-mv:hover{background:var(--gr-s2); color:var(--accent);}
.ins-root .ins-gr-mv.is-on{color:var(--accent);}
.ins-root .ins-gr-mv.is-on b{text-decoration:underline;}
.ins-root .ins-gr-mdc{display:inline-block; font-style:normal; margin-left:6px; padding:0 4px; border-radius:4px; font-size:10px;
  font-weight:700; background:var(--gr-s2); vertical-align:1px;}
.ins-root .ins-gr-mdc.is-p, .ins-root .ins-gr-stack em.is-p{color:var(--div-pos);}
.ins-root .ins-gr-mdc.is-m, .ins-root .ins-gr-stack em.is-m{color:var(--div-neg);}
.ins-root .ins-gr-mdc.is-z, .ins-root .ins-gr-stack em.is-z{color:var(--text-muted); font-weight:400;}
/* Полоска под числом: дорожка это размах уровня, заливка значение строки, засечка медиана уровня. */
.ins-root .ins-gr-mdb{position:absolute; left:12px; right:8px; bottom:2px; height:5px; border-radius:3px;
  background:color-mix(in oklab, var(--text-muted) 20%, transparent);}
.ins-root .ins-gr-mdb::before{content:""; position:absolute; left:0; bottom:0; height:5px; border-radius:3px; width:var(--v);
  background:color-mix(in oklab, var(--accent) 55%, transparent);}
.ins-root .ins-gr-mdb::after{content:""; position:absolute; bottom:-2px; left:var(--m); width:2px; height:9px; margin-left:-1px;
  border-radius:1px; background:var(--text-primary); opacity:.7;}
.ins-root .ins-gr-mdb.is-on::after{width:3px; height:11px; bottom:-3px; opacity:1; background:var(--accent);}
.ins-root .ins-gr-mdleg{display:flex; flex-wrap:wrap; align-items:center; gap:8px 22px; border:1px solid var(--border);
  border-radius:10px; background:var(--gr-s2); padding:9px 12px; margin-bottom:10px; font-size:12px; color:var(--text-secondary);}
.ins-root .ins-gr-mdleg > b{color:var(--text-primary);}
.ins-root .ins-gr-it{display:flex; align-items:center; gap:7px;}
.ins-root .ins-gr-sam{display:inline-flex; align-items:center; justify-content:center; min-width:30px; height:20px; border-radius:5px;
  background:var(--surface-1); border:1px solid var(--border); font-variant-numeric:tabular-nums; font-size:11.5px; font-weight:600; position:relative;}
.ins-root .ins-gr-sambar{width:96px; min-width:96px;}
.ins-root .ins-gr-sambar i{position:absolute; left:5px; right:5px; bottom:6px; height:5px; border-radius:3px;
  background:color-mix(in oklab, var(--text-muted) 20%, transparent);}
.ins-root .ins-gr-sambar i::before{content:""; position:absolute; left:0; bottom:0; height:5px; border-radius:3px; width:58%;
  background:color-mix(in oklab, var(--accent) 55%, transparent);}
.ins-root .ins-gr-sambar i::after{content:""; position:absolute; bottom:-2px; left:38%; width:2px; height:9px; background:var(--text-primary); opacity:.7;}

/* дерево: треугольник, отступ уровня и убывающий вес строки */
.ins-root .ins-gr-tw{border:0; background:none; cursor:pointer; font:inherit; font-size:11px; padding:0; color:var(--text-muted); line-height:1;}
.ins-root .ins-gr-tw:hover{color:var(--text-primary);}
.ins-root .ins-gr-tw-none{display:inline-block; width:8px;}
.ins-root .ins-gr-lv1 td.ins-gr-name .ins-gr-nm{font-weight:600;}
.ins-root .ins-gr-lv2 td.ins-gr-name .ins-gr-nm{font-weight:400; color:var(--text-secondary);}
.ins-root .ins-gr-main tr.ins-gr-lv1 td, .ins-root .ins-gr-main tr.ins-gr-lv2 td{opacity:.94;}
.ins-root .ins-gr-sw{width:9px; height:9px; border-radius:3px; display:inline-block; flex:0 0 auto;}
.ins-root .ins-gr-main tbody tr{cursor:pointer;}
.ins-root .ins-gr-main tbody tr:hover td.ins-gr-name{color:var(--accent);}
.ins-root .ins-gr-main tr.is-ref td{background:var(--gr-s2);}
.ins-root .ins-gr-main tr.is-ref td:first-child{box-shadow:inset 3px 0 0 var(--accent);}
.ins-root .ins-gr-main tr.is-ref td.ins-gr-name .ins-gr-ttl{color:var(--accent); font-weight:700;}
.ins-root .ins-gr-refmark{display:inline-block; margin-left:6px; padding:1px 6px; border-radius:999px; font-size:10px; font-weight:700;
  letter-spacing:.03em; text-transform:uppercase; background:var(--accent); color:var(--on-accent, #fff); font-style:normal; vertical-align:1px;}
.ins-root .ins-gr-t td.ins-gr-cell{padding:2px;}
.ins-root .ins-gr-cell div{border-radius:4px; height:26px; line-height:26px; font-size:11.5px; position:relative; overflow:hidden;}
.ins-root .ins-gr-nm{display:block; font-weight:600;}
.ins-root .ins-gr-row{display:flex; flex-wrap:nowrap; align-items:baseline; column-gap:6px;}
.ins-root .ins-gr-row > .ins-gr-tw, .ins-root .ins-gr-row > .ins-gr-tw-none{flex:0 0 auto;}
.ins-root .ins-gr-ttl{flex:1 1 auto; min-width:0;}
.ins-root .ins-gr-nm s{display:block; padding-left:14px; text-decoration:none; font-weight:400; font-size:11px; color:var(--text-muted);}

/* силуэт родителя: полоска по низу клетки, кусок от своей доли до родительской */
.ins-root .ins-gr-gh{position:absolute; left:0; right:0; bottom:0; height:6px; pointer-events:none;
  background:color-mix(in oklab, currentColor 16%, transparent);}
.ins-root .ins-gr-gh::before{content:""; position:absolute; left:0; bottom:0; height:6px; width:var(--own); background:currentColor; opacity:.3;}
.ins-root .ins-gr-gh::after{content:""; position:absolute; bottom:0; height:6px; left:var(--from); width:var(--len); background:var(--ghc);
  box-shadow:inset 0 0 0 1px rgba(0,0,0,.22);}

/* --surface-2 из П: фон стопки, опоры, чипов. Не --cell-0, иначе клетки «100» (--seq-0) и нули опоры сливаются с фоном */
.ins-root .ins-gr{--gr-s2:#f4f3f0;}
:root[data-theme="dark"] .ins-root .ins-gr{--gr-s2:#232322;}
.ins-root .ins-gr-stack{border:1px solid var(--border); border-radius:12px; background:var(--gr-s2); padding:12px 14px; margin-bottom:12px; overflow-x:auto;}
.ins-root .ins-gr-stack table{margin-top:8px;}
.ins-root .ins-gr-main tr.is-sub td.ins-gr-name{color:var(--text-secondary);}
.ins-root .ins-gr-stack td.ins-gr-name{max-width:340px;}
.ins-root .ins-gr-stack td.ins-gr-name b{font-weight:650;}
.ins-root .ins-gr-stack td.ins-gr-name s{display:block; text-decoration:none; font-size:11px; color:var(--text-muted);}
.ins-root .ins-gr-stack td.ins-gr-cell div{height:30px; line-height:30px;}
.ins-root .ins-gr-stack em{display:inline-block; font-style:normal; font-size:10px; line-height:14px; margin-left:5px; vertical-align:1px;
  padding:0 4px; border-radius:4px; background:var(--surface-1); font-weight:700;}
.ins-root .ins-gr-lvtag{display:inline-block; margin-right:6px; padding:1px 6px; border-radius:999px; font-size:10px; font-weight:700;
  background:var(--surface-1); color:var(--text-muted); border:1px solid var(--border);}

/* сборки: пунктир, подъём/выдвижение, инструменты, ореол */
.ins-root .ins-gr-main tr.is-asm td, .ins-root .ins-gr-main tr.is-asmch td{background:var(--surface-1);}
.ins-root .ins-gr-main tr.is-asm.mk-plain td{box-shadow:inset 0 1px 0 var(--text-muted);}
.ins-root .ins-gr-main tr.is-asm.mk-plain:not(:has(+ tr.is-asmch)) td{box-shadow:inset 0 1px 0 var(--text-muted), inset 0 -1px 0 var(--text-muted);}
.ins-root .ins-gr-main tr.is-asmch.mk-plain:not(:has(+ tr.is-asmch)) td{box-shadow:inset 0 -1px 0 var(--text-muted);}
.ins-root .ins-gr-main tr.is-asm.mk-out td, .ins-root .ins-gr-main tr.is-asmch.mk-out td{transform:translateX(-6px);}
.ins-root .ins-gr-main tr.is-asm td.ins-gr-name .ins-gr-nm, .ins-root .ins-gr-main tr.is-asmch td.ins-gr-name .ins-gr-ttl{font-style:italic;}
.ins-root .ins-gr-main tr.is-pickon td.ins-gr-name{cursor:copy;}
.ins-root .ins-gr-main tr.is-picked td.ins-gr-name .ins-gr-ttl{color:var(--accent); text-decoration:underline;}
.ins-root .ins-gr-tools{display:inline-flex; gap:1px; margin-left:6px; flex:0 0 auto;}
.ins-root .ins-gr-tb{border:0; background:none; cursor:pointer; font:inherit; font-size:11px; line-height:1; color:var(--text-muted); padding:2px 4px; border-radius:5px;}
.ins-root .ins-gr-tb:hover{color:var(--text-primary); background:var(--gr-s2);}
.ins-root .ins-gr-pin{display:inline-flex; align-items:center; transform:rotate(-20deg);}
.ins-root .ins-gr-pin.is-on{color:var(--accent); transform:none;}
.ins-root .ins-gr-halo{position:absolute; pointer-events:none; z-index:0; border-radius:8px;
  box-shadow:0 0 0 3px var(--gr-s2), 0 0 14px 3px rgba(42,120,214,.28), 0 6px 16px -6px rgba(0,0,0,.34);}
.ins-root .ins-gr-halo.is-out{box-shadow:0 0 0 3px var(--gr-s2), 0 0 20px 5px rgba(42,120,214,.34), 0 12px 22px -8px rgba(0,0,0,.44);}

.ins-root .ins-gr-tip{position:fixed; z-index:60; pointer-events:none; background:var(--text-primary); color:var(--page);
  font-size:12px; line-height:1.45; border:1px solid rgba(128,128,128,.35); border-radius:8px; padding:8px 11px; max-width:280px;
  box-shadow:0 8px 24px rgba(0,0,0,.38), 0 2px 6px rgba(0,0,0,.28); opacity:0; transition:opacity .1s;}
.ins-root .ins-gr-tip.is-on{opacity:1;}
.ins-root .ins-gr-tip b{display:block; font-size:12.5px;}
.ins-root .ins-gr-raw{margin-top:8px;}
.ins-root .ins-gr-raw summary{cursor:pointer; font-size:13px; color:var(--text-secondary);}
.ins-root .ins-gr-raw table{margin-top:10px;}
</style>
